import { z } from 'zod';
import { RegistryBlockKeySchema } from '../catalog/platformCatalogIds';
import { DataBindingSchema } from './dataBinding';
import { FieldBindingsSchema } from './fieldBindings';

/** BCP 47-style lowercase language tags (e.g. `en`, `de`, `vi`, `en-us`). */
export const LOCALE_KEY_PATTERN = /^[a-z]{2}(-[a-z0-9]{2,8})*$/;

/** Strict global E.164 telephone format (leading `+`, 8–15 digits total). */
export const E164_TELEPHONE_PATTERN = /^\+[1-9]\d{7,14}$/;

/** ISO 3166-1 alpha-2 country code (uppercase). */
export const ISO3166_ALPHA2_PATTERN = /^[A-Z]{2}$/;

const localeKeySchema = z
  .string()
  .regex(LOCALE_KEY_PATTERN, {
    message: 'Locale key must be a lowercase BCP 47 language tag (e.g. en, de, vi, en-us)',
  });

/**
 * Multi-language string map — keys are lowercase locale tags, values are plain strings.
 */
export const LocalizedStringSchema = z.record(localeKeySchema, z.string());

/** Canvas component slugs — keys of `BlockRenderer` and `LayoutBlock.componentType`. */
export const LAYOUT_COMPONENT_TYPE = {
  navigationHeader: 'Navigation_Header',
  heroEditorialSplit: 'Hero_Editorial_Split',
  menuGrid3Column: 'MenuGrid_3Column',
  dynamicMenuFlow: 'Dynamic_Menu_Flow',
  reservationFormEmbed: 'Reservation_Form_Embed',
  reservationThirdPartyBook: 'Reservation_Third_Party_Book',
  reservationMessengerBook: 'Reservation_Messenger_Book',
  reservationCallBook: 'Reservation_Call_Book',
  chefStoryBlock: 'Chef_Story_Block',
  kitchenTeamBlock: 'Kitchen_Team_Block',
  imageGallery: 'Image_Gallery',
  tastingMenuCourseTimeline: 'Tasting_Menu_Course_Timeline',
  allergyCaptureSecureForm: 'Allergy_Capture_Secure_Form',
  heroVideoAtmosphere: 'Hero_Video_Atmosphere',
  footerStandard: 'Footer_Standard',
  legalImprintBlock: 'Legal_Imprint_Block',
  legalPrivacyBlock: 'Legal_Privacy_Block',
  simpleOpeningHours: 'Simple_Opening_Hours',
  addressMapBlock: 'Address_Map_Block',
  drinksListStatic: 'Drinks_List_Static',
  staticLocationText: 'Static_Location_Text',
  simpleMenuStrip: 'Simple_Menu_Strip',
  liveBakeryCounterList: 'Live_Bakery_Counter_List',
  socialFeedGrid: 'Social_Feed_Grid',
  coffeeSubscriptionMatrix: 'Coffee_Subscription_Matrix',
  preOrderPickupForm: 'Pre_Order_Pickup_Form',
  dynamicCocktailSpotlight: 'Dynamic_Cocktail_Spotlight',
  privatePartyInquiryForm: 'Private_Party_Inquiry_Form',
  liveTapListSync: 'Live_Tap_List_Sync',
  vipTableBookingStrip: 'VIP_Table_Booking_Strip',
  weeklyScheduleRouteStrip: 'Weekly_Schedule_Route_Strip',
  cateringInquiryForm: 'Catering_Inquiry_Form',
  liveGpsLocationEmbed: 'Live_GPS_Location_Embed',
  popUpTicketPurchaseCard: 'PopUp_Ticket_Purchase_Card',
  contactBlock: 'Contact_Block',
  faqBlock: 'Faq_Block',
  /** Global chrome — scroll-to-top when the visitor leaves the first viewport. */
  backToTopFloat: 'BackToTop_Float',
  /** Global chrome — floating phone + social channel bar. */
  contactChannelsFloat: 'ContactChannels_Float',
  /** Global chrome — thin utility strip above navigation. */
  utilityBar: 'Utility_Bar',
  /** Global chrome — temporary site-wide notice above navigation. */
  announcementBar: 'Announcement_Bar',
} as const;

export const LAYOUT_COMPONENT_TYPE_VALUES = [
  LAYOUT_COMPONENT_TYPE.navigationHeader,
  LAYOUT_COMPONENT_TYPE.heroEditorialSplit,
  LAYOUT_COMPONENT_TYPE.menuGrid3Column,
  LAYOUT_COMPONENT_TYPE.dynamicMenuFlow,
  LAYOUT_COMPONENT_TYPE.reservationFormEmbed,
  LAYOUT_COMPONENT_TYPE.reservationThirdPartyBook,
  LAYOUT_COMPONENT_TYPE.reservationMessengerBook,
  LAYOUT_COMPONENT_TYPE.reservationCallBook,
  LAYOUT_COMPONENT_TYPE.chefStoryBlock,
  LAYOUT_COMPONENT_TYPE.kitchenTeamBlock,
  LAYOUT_COMPONENT_TYPE.imageGallery,
  LAYOUT_COMPONENT_TYPE.tastingMenuCourseTimeline,
  LAYOUT_COMPONENT_TYPE.allergyCaptureSecureForm,
  LAYOUT_COMPONENT_TYPE.heroVideoAtmosphere,
  LAYOUT_COMPONENT_TYPE.footerStandard,
  LAYOUT_COMPONENT_TYPE.legalImprintBlock,
  LAYOUT_COMPONENT_TYPE.legalPrivacyBlock,
  LAYOUT_COMPONENT_TYPE.simpleOpeningHours,
  LAYOUT_COMPONENT_TYPE.addressMapBlock,
  LAYOUT_COMPONENT_TYPE.drinksListStatic,
  LAYOUT_COMPONENT_TYPE.staticLocationText,
  LAYOUT_COMPONENT_TYPE.simpleMenuStrip,
  LAYOUT_COMPONENT_TYPE.liveBakeryCounterList,
  LAYOUT_COMPONENT_TYPE.socialFeedGrid,
  LAYOUT_COMPONENT_TYPE.coffeeSubscriptionMatrix,
  LAYOUT_COMPONENT_TYPE.preOrderPickupForm,
  LAYOUT_COMPONENT_TYPE.dynamicCocktailSpotlight,
  LAYOUT_COMPONENT_TYPE.privatePartyInquiryForm,
  LAYOUT_COMPONENT_TYPE.liveTapListSync,
  LAYOUT_COMPONENT_TYPE.vipTableBookingStrip,
  LAYOUT_COMPONENT_TYPE.weeklyScheduleRouteStrip,
  LAYOUT_COMPONENT_TYPE.cateringInquiryForm,
  LAYOUT_COMPONENT_TYPE.liveGpsLocationEmbed,
  LAYOUT_COMPONENT_TYPE.popUpTicketPurchaseCard,
  LAYOUT_COMPONENT_TYPE.contactBlock,
  LAYOUT_COMPONENT_TYPE.faqBlock,
  LAYOUT_COMPONENT_TYPE.backToTopFloat,
  LAYOUT_COMPONENT_TYPE.contactChannelsFloat,
  LAYOUT_COMPONENT_TYPE.utilityBar,
  LAYOUT_COMPONENT_TYPE.announcementBar,
] as const;

export const LayoutComponentTypeSchema = z.enum(LAYOUT_COMPONENT_TYPE_VALUES);

/**
 * Block media values: absolute http(s) URLs (stock/external) **or** same-origin
 * path-only owned assets (`/uploads/…`, `/platform/defaults/…`).
 * Never require a CDN host in stored content.
 */
export const optimizedMediaUrlSchema = z
  .string()
  .min(1)
  .refine(
    (value) => {
      if (value.startsWith('/uploads/') || value.startsWith('/platform/defaults/')) {
        return !value.includes('://') && !value.includes('\\');
      }
      try {
        const parsed = new URL(value);
        return parsed.protocol === 'http:' || parsed.protocol === 'https:';
      } catch {
        return false;
      }
    },
    { message: 'Media URL must be a valid optimized asset URL' },
  );

/**
 * Structural canvas block with localized content fields and ordered display.
 * `pageId`: `null` = global nav/footer; UUID = page canvas; `undefined` = legacy v4.1 row.
 */
export const LayoutBlockSchema = z
  .object({
    blockId: z.string().uuid({ message: 'blockId must be a valid UUID' }),
    pageId: z.string().uuid().nullable().optional(),
    componentType: LayoutComponentTypeSchema,
    /** Tier-matrix key when `componentType` is a shared canvas implementation (e.g. cafe hero → Hero_Editorial_Split). */
    registryBlockKey: RegistryBlockKeySchema.optional(),
    content: z.record(z.string().min(1), LocalizedStringSchema),
    media: z.record(z.string().min(1), optimizedMediaUrlSchema),
    displayOrder: z.number().int(),
    /**
     * When true, block stays in draft/studio trees but is omitted from
     * preview compose and live bake. Omitted / false = visible.
     */
    hidden: z.boolean().optional(),
    /** ODL reference — menu catalog, form, or floor plan (§4.4). */
    dataBinding: DataBindingSchema.optional(),
    /** PDL/config field source map — role key → binding ref (§8.7). */
    fieldBindings: FieldBindingsSchema.optional(),
  })
  .superRefine((block, ctx) => {
    if (block.pageId === undefined) {
      return;
    }
    const isGlobalScope = block.pageId === null;
    const isGlobalComponent =
      block.componentType === LAYOUT_COMPONENT_TYPE.navigationHeader ||
      block.componentType === LAYOUT_COMPONENT_TYPE.footerStandard ||
      block.componentType === LAYOUT_COMPONENT_TYPE.backToTopFloat ||
      block.componentType === LAYOUT_COMPONENT_TYPE.contactChannelsFloat ||
      block.componentType === LAYOUT_COMPONENT_TYPE.utilityBar ||
      block.componentType === LAYOUT_COMPONENT_TYPE.announcementBar;
    if (isGlobalScope !== isGlobalComponent) {
      ctx.addIssue({
        code: 'custom',
        message:
          'Global scope (pageId null) is limited to Navigation_Header, Footer_Standard, BackToTop_Float, ContactChannels_Float, Utility_Bar, and Announcement_Bar',
      });
    }
  });

export const PriceRangeSchema = z.enum(['$', '$$', '$$$', '$$$$']);

export const DayOfWeekSchema = z.enum([
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
]);

const timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, {
  message: 'Time must be HH:MM in 24-hour format',
});

/** Schema.org OpeningHoursSpecification — layered day / opens / closes maps */
export const OpeningHoursSpecificationSchema = z.object({
  dayOfWeek: z.union([DayOfWeekSchema, z.array(DayOfWeekSchema).min(1)]),
  opens: timeOfDaySchema,
  closes: timeOfDaySchema,
});

export const LocalBusinessAddressSchema = z.object({
  streetAddress: z.string().min(1),
  addressLocality: z.string().min(1),
  addressRegion: z.string().min(1),
  postalCode: z.string().min(1),
  addressCountry: z
    .string()
    .length(2, { message: 'addressCountry must be exactly 2 characters (ISO 3166-1 alpha-2)' })
    .regex(ISO3166_ALPHA2_PATTERN, {
      message: 'addressCountry must be an uppercase ISO 3166-1 alpha-2 code (e.g. US, DE, VN)',
    }),
});

export const GeoCoordinatesSchema = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
});

const optionalHttpUrlSchema = z.union([z.string().url(), z.literal('')]).optional();

/** Curated social profiles — aligns with SITE_SOCIAL_ICONS on customer sites. */
export const LocalBusinessSocialLinksSchema = z.object({
  facebook: optionalHttpUrlSchema,
  instagram: optionalHttpUrlSchema,
  tiktok: optionalHttpUrlSchema,
  x: optionalHttpUrlSchema,
  youtube: optionalHttpUrlSchema,
});

const optionalContactNoteSchema = z.string().trim().max(80).optional();

export const BrandingTelephoneEntrySchema = z.object({
  telephone: z.string().regex(E164_TELEPHONE_PATTERN, {
    message: 'telephone must be a valid E.164 number (e.g. +14155552671)',
  }),
  /** Optional role note — e.g. Reservations, Kitchen. */
  description: optionalContactNoteSchema,
});

export const BrandingEmailEntrySchema = z.object({
  email: z.string().email(),
  /** Optional role note — e.g. Support, Press. */
  description: optionalContactNoteSchema,
});

/** Extra guest-facing business location (store, second venue, …). */
export const BrandingBusinessAddressEntrySchema = z.object({
  address: LocalBusinessAddressSchema,
  /** Optional role note — e.g. Main dining room, Takeaway counter. */
  description: optionalContactNoteSchema,
});

function coerceTelephoneEntries(value: unknown): unknown {
  if (value == null) return undefined;
  if (!Array.isArray(value)) return value;
  return value.map((item) => (typeof item === 'string' ? { telephone: item } : item));
}

function coerceEmailEntries(value: unknown): unknown {
  if (value == null) return undefined;
  if (!Array.isArray(value)) return value;
  return value.map((item) => (typeof item === 'string' ? { email: item } : item));
}

/** Max contact / store-address entries (primary + additional). */
export const BRANDING_CONTACT_SLOT_COUNT = 5 as const;

const BRANDING_ADDITIONAL_CONTACT_SLOT_COUNT = BRANDING_CONTACT_SLOT_COUNT - 1;

/**
 * Checkout / branding intercept payload — legal/contact + GEO discoverability for local business SEO.
 */
export const LocalBusinessBrandingSchema = z.object({
  legalName: z
    .string()
    .min(2, { message: 'legalName must be at least 2 characters' })
    .max(100, { message: 'legalName must be at most 100 characters' }),
  /** Primary public phone (E.164). Extra numbers go in `additionalTelephones`. */
  telephone: z.string().regex(E164_TELEPHONE_PATTERN, {
    message: 'telephone must be a valid E.164 number (e.g. +14155552671)',
  }),
  /** Optional note for the primary phone. */
  telephoneDescription: optionalContactNoteSchema,
  /** Extra public phones (not including `telephone`). Accepts legacy string[] via coerce. */
  additionalTelephones: z.preprocess(
    coerceTelephoneEntries,
    z
      .array(BrandingTelephoneEntrySchema)
      .max(BRANDING_ADDITIONAL_CONTACT_SLOT_COUNT, {
        message: `additionalTelephones accepts at most ${BRANDING_ADDITIONAL_CONTACT_SLOT_COUNT} numbers`,
      })
      .optional(),
  ),
  priceRange: PriceRangeSchema,
  /**
   * Primary guest-facing business location (JSON-LD / GBP).
   * Extra venues go in `additionalBusinessAddresses` (same pattern as emails).
   */
  address: LocalBusinessAddressSchema,
  /** Optional note for the primary business address. */
  addressDescription: optionalContactNoteSchema,
  /** Extra public business locations (not including `address`). */
  additionalBusinessAddresses: z
    .array(BrandingBusinessAddressEntrySchema)
    .max(BRANDING_ADDITIONAL_CONTACT_SLOT_COUNT, {
      message: `additionalBusinessAddresses accepts at most ${BRANDING_ADDITIONAL_CONTACT_SLOT_COUNT} locations`,
    })
    .optional(),
  /**
   * Singular Impressum / registry postal address.
   * When omitted, imprint/privacy fall back to the primary business address.
   */
  legalAddress: LocalBusinessAddressSchema.optional(),
  /**
   * Singular Impressum contact phone (E.164).
   * When omitted, imprint falls back to the primary public `telephone`.
   */
  legalTelephone: z
    .string()
    .regex(E164_TELEPHONE_PATTERN, {
      message: 'legalTelephone must be a valid E.164 number (e.g. +14155552671)',
    })
    .optional(),
  openingHours: z.array(OpeningHoursSpecificationSchema).optional(),
  /** Primary public contact email — footer + Datenschutz (PDL). */
  contactEmail: z.union([z.string().email(), z.literal('')]).optional(),
  /** Optional note for the primary email. */
  contactEmailDescription: optionalContactNoteSchema,
  /** Extra public emails (not including `contactEmail`). Accepts legacy string[] via coerce. */
  additionalContactEmails: z.preprocess(
    coerceEmailEntries,
    z
      .array(BrandingEmailEntrySchema)
      .max(BRANDING_ADDITIONAL_CONTACT_SLOT_COUNT, {
        message: `additionalContactEmails accepts at most ${BRANDING_ADDITIONAL_CONTACT_SLOT_COUNT} addresses`,
      })
      .optional(),
  ),
  /**
   * Singular Impressum / Privacy contact email.
   * When omitted, imprint/privacy fall back to the primary public `contactEmail`.
   */
  legalContactEmail: z.union([z.string().email(), z.literal('')]).optional(),
  representative: z.string().max(200).optional(),
  vatId: z.string().max(80).optional(),
  registerCourt: z.string().max(120).optional(),
  registerNumber: z.string().max(80).optional(),
  /** Canonical guest homepage — Schema.org `url`. */
  url: optionalHttpUrlSchema,
  /** Citation-ready blurb — Schema.org `description`. */
  description: z.union([z.string().trim().max(500), z.literal('')]).optional(),
  /** Specialty / cuisine tags — Schema.org `knowsAbout` (+ `servesCuisine` for food verticals). */
  knowsAbout: z.array(z.string().trim().min(1).max(60)).max(12).optional(),
  socialLinks: LocalBusinessSocialLinksSchema.optional(),
  /** Schema.org LocalBusiness `image` (HTTPS URL; not og:image meta). */
  image: optionalHttpUrlSchema,
  /** Studio-only Maps import URL recall — not emitted in Schema.org JSON-LD. */
  googleMapsUrl: z.union([z.string().trim().max(2048), z.literal('')]).optional(),
  geo: GeoCoordinatesSchema.optional(),
});

export type LocalizedString = z.infer<typeof LocalizedStringSchema>;
export type LayoutComponentType = z.infer<typeof LayoutComponentTypeSchema>;
export type LayoutBlock = z.infer<typeof LayoutBlockSchema>;
export type PriceRange = z.infer<typeof PriceRangeSchema>;
export type LocalBusinessAddress = z.infer<typeof LocalBusinessAddressSchema>;
export type DayOfWeek = z.infer<typeof DayOfWeekSchema>;
export type OpeningHoursSpecification = z.infer<typeof OpeningHoursSpecificationSchema>;
export type GeoCoordinates = z.infer<typeof GeoCoordinatesSchema>;
export type LocalBusinessSocialLinks = z.infer<typeof LocalBusinessSocialLinksSchema>;
export type BrandingTelephoneEntry = z.infer<typeof BrandingTelephoneEntrySchema>;
export type BrandingEmailEntry = z.infer<typeof BrandingEmailEntrySchema>;
export type BrandingBusinessAddressEntry = z.infer<typeof BrandingBusinessAddressEntrySchema>;
export type LocalBusinessBranding = z.infer<typeof LocalBusinessBrandingSchema>;

/** Address identity key for dedupe (street + locality + postal + country). */
function brandingAddressDedupeKey(address: LocalBusinessAddress): string {
  return [
    address.streetAddress,
    address.postalCode,
    address.addressLocality,
    address.addressRegion,
    address.addressCountry,
  ]
    .map((part) => (typeof part === 'string' ? part : '').trim().toLowerCase())
    .join('|');
}

/** Primary + additional business locations with optional notes, deduped, primary first. */
export function listBrandingBusinessAddressEntries(
  branding: LocalBusinessBranding,
): BrandingBusinessAddressEntry[] {
  const seen = new Set<string>();
  const out: BrandingBusinessAddressEntry[] = [];
  const push = (address: LocalBusinessAddress | undefined, description?: string) => {
    if (!address?.streetAddress?.trim()) return;
    const key = brandingAddressDedupeKey(address);
    if (seen.has(key)) return;
    seen.add(key);
    const note = description?.trim();
    out.push(note ? { address, description: note } : { address });
  };
  push(branding.address, branding.addressDescription);
  for (const entry of branding.additionalBusinessAddresses ?? []) {
    push(entry.address, entry.description);
  }
  return out;
}

/** Singular legal address, falling back to primary business location when unset. */
export function resolveBrandingLegalAddress(
  branding: LocalBusinessBranding,
): LocalBusinessAddress | null {
  return branding.legalAddress ?? branding.address ?? null;
}

/** Singular legal phone, falling back to primary public telephone when unset. */
export function resolveBrandingLegalTelephone(
  branding: LocalBusinessBranding,
): string {
  const legal = branding.legalTelephone?.trim();
  if (legal) return legal;
  return branding.telephone?.trim() ?? '';
}

/** Singular legal email, falling back to primary public contact email when unset. */
export function resolveBrandingLegalContactEmail(
  branding: LocalBusinessBranding,
): string {
  const legal = branding.legalContactEmail?.trim();
  if (legal) return legal;
  return branding.contactEmail?.trim() ?? '';
}

/** Primary + additional phones with optional notes, deduped, primary first. */
export function listBrandingTelephoneEntries(
  branding: LocalBusinessBranding,
): BrandingTelephoneEntry[] {
  const seen = new Set<string>();
  const out: BrandingTelephoneEntry[] = [];
  const push = (telephone: string | undefined, description?: string) => {
    const value = telephone?.trim();
    if (!value || seen.has(value)) return;
    seen.add(value);
    const note = description?.trim();
    out.push(note ? { telephone: value, description: note } : { telephone: value });
  };
  push(branding.telephone, branding.telephoneDescription);
  for (const entry of branding.additionalTelephones ?? []) {
    if (typeof entry === 'string') {
      push(entry);
      continue;
    }
    push(entry.telephone, entry.description);
  }
  return out;
}

/** Primary + additional phones, deduped, primary first. */
export function listBrandingTelephones(branding: LocalBusinessBranding): string[] {
  return listBrandingTelephoneEntries(branding).map((entry) => entry.telephone);
}

/** Primary + additional emails with optional notes, deduped, primary first. */
export function listBrandingEmailEntries(branding: LocalBusinessBranding): BrandingEmailEntry[] {
  const seen = new Set<string>();
  const out: BrandingEmailEntry[] = [];
  const push = (email: string | undefined, description?: string) => {
    const trimmed = email?.trim();
    if (!trimmed) return;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    const note = description?.trim();
    out.push(note ? { email: trimmed, description: note } : { email: trimmed });
  };
  push(branding.contactEmail, branding.contactEmailDescription);
  for (const entry of branding.additionalContactEmails ?? []) {
    if (typeof entry === 'string') {
      push(entry);
      continue;
    }
    push(entry.email, entry.description);
  }
  return out;
}

/** Primary + additional emails, deduped, primary first (skips empty primary). */
export function listBrandingContactEmails(branding: LocalBusinessBranding): string[] {
  return listBrandingEmailEntries(branding).map((entry) => entry.email);
}

/**
 * Token slug from a contact/location description.
 * Lowercase and strip spaces (and other non-alphanumerics): "Customer Care" → `customercare`.
 */
export function brandingTokenSlug(
  description: string | undefined | null,
  fallback = 'primary',
): string {
  const slug = (description ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/[^a-z0-9]/g, '');
  return slug || fallback;
}

/** Assign unique slugs to a list of optional descriptions (dedupe with numeric suffix). */
export function assignBrandingTokenSlugs(
  descriptions: ReadonlyArray<string | undefined | null>,
): string[] {
  const seen = new Map<string, number>();
  return descriptions.map((description, index) => {
    const base = brandingTokenSlug(description, index === 0 ? 'primary' : `line${index + 1}`);
    const count = (seen.get(base) ?? 0) + 1;
    seen.set(base, count);
    return count === 1 ? base : `${base}${count}`;
  });
}

export type SluggedTelephoneEntry = BrandingTelephoneEntry & { slug: string };
export type SluggedEmailEntry = BrandingEmailEntry & { slug: string };
export type SluggedBusinessAddressEntry = BrandingBusinessAddressEntry & { slug: string };

export function listSluggedBrandingTelephones(
  branding: LocalBusinessBranding,
): SluggedTelephoneEntry[] {
  const entries = listBrandingTelephoneEntries(branding);
  const slugs = assignBrandingTokenSlugs(entries.map((e) => e.description));
  return entries.map((entry, i) => ({ ...entry, slug: slugs[i]! }));
}

export function listSluggedBrandingEmails(
  branding: LocalBusinessBranding,
): SluggedEmailEntry[] {
  const entries = listBrandingEmailEntries(branding);
  const slugs = assignBrandingTokenSlugs(entries.map((e) => e.description));
  return entries.map((entry, i) => ({ ...entry, slug: slugs[i]! }));
}

export function listSluggedBrandingBusinessAddresses(
  branding: LocalBusinessBranding,
): SluggedBusinessAddressEntry[] {
  const entries = listBrandingBusinessAddressEntries(branding);
  const slugs = assignBrandingTokenSlugs(entries.map((e) => e.description));
  return entries.map((entry, i) => ({ ...entry, slug: slugs[i]! }));
}

export function findBrandingTelephoneBySlug(
  branding: LocalBusinessBranding,
  slug: string,
): BrandingTelephoneEntry | null {
  const entries = listSluggedBrandingTelephones(branding);
  const hit = entries.find((e) => e.slug === slug);
  if (hit) return hit;
  if (slug === 'primary') return entries[0] ?? null;
  return null;
}

export function findBrandingEmailBySlug(
  branding: LocalBusinessBranding,
  slug: string,
): BrandingEmailEntry | null {
  const entries = listSluggedBrandingEmails(branding);
  const hit = entries.find((e) => e.slug === slug);
  if (hit) return hit;
  if (slug === 'primary') return entries[0] ?? null;
  return null;
}

export function findBrandingBusinessAddressBySlug(
  branding: LocalBusinessBranding,
  slug: string,
): BrandingBusinessAddressEntry | null {
  const entries = listSluggedBrandingBusinessAddresses(branding);
  const hit = entries.find((e) => e.slug === slug);
  if (hit) return hit;
  if (slug === 'primary') return entries[0] ?? null;
  return null;
}

/** Single-line postal display for a structured address. */
export function formatBrandingPostalAddress(
  address: LocalBusinessAddress | null | undefined,
): string {
  if (!address || typeof address !== 'object') {
    return '';
  }
  // Coerce — persisted / import payloads can carry nullish fields despite the schema.
  const streetAddress = address.streetAddress?.trim() ?? '';
  const postalCode = address.postalCode?.trim() ?? '';
  const addressLocality = address.addressLocality?.trim() ?? '';
  const addressRegion = address.addressRegion?.trim() ?? '';
  const addressCountry = address.addressCountry?.trim() ?? '';
  const locality = [postalCode, addressLocality].filter(Boolean).join(' ');
  const regionDistinct =
    addressRegion && addressRegion.toLowerCase() !== addressLocality.toLowerCase()
      ? addressRegion
      : '';
  const region = regionDistinct ? `, ${regionDistinct}` : '';
  const country = addressCountry ? `, ${addressCountry}` : '';
  return [streetAddress, `${locality}${region}${country}`.replace(/^,\s*/, '')]
    .filter(Boolean)
    .join(', ')
    .trim();
}

/** Format legal address (falls back to primary business). */
export function formatBrandingLegalAddress(branding: LocalBusinessBranding): string {
  return formatBrandingPostalAddress(resolveBrandingLegalAddress(branding));
}

/** Format one business-address entry (with optional description). */
export function formatBrandingBusinessAddressEntry(
  entry: BrandingBusinessAddressEntry,
): string {
  const line = formatBrandingPostalAddress(entry.address);
  return entry.description ? `${line} (${entry.description})` : line;
}
